// src/components/privacy/ZKPAErrorBoundary.tsx
import React from "react";
import { Text, Button } from "@opal/components";
import { SvgShieldAlert } from "@opal/icons";
import { ZKPAError } from "@/shared/lib/crypto/errors";

interface State {
  hasError: boolean;
  errorMessage: string;
}

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * ZKPAErrorBoundary — catches crypto errors thrown during render.
 * Wrap around any route/component that uses ZKPA crypto operations.
 */
export class ZKPAErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }

  static getDerivedStateFromError(error: unknown): State {
    const message =
      error instanceof ZKPAError
        ? `[${error.code}] ${error.message}`
        : error instanceof Error
          ? error.message
          : "Lỗi bảo mật không xác định";
    return { hasError: true, errorMessage: message };
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div
          className="flex items-center justify-center min-h-[200px] p-6"
          role="alert"
          aria-live="assertive"
        >
          <div className="text-center space-y-3 max-w-sm">
            <SvgShieldAlert
              className="h-10 w-10 text-[var(--status-error-02)] mx-auto"
              aria-hidden="true"
            />
            <Text size="lg" weight="semibold">Lỗi bảo mật</Text>
            <Text size="xs" color="subtle" className="font-mono">
              {this.state.errorMessage}
            </Text>
            <Button
              id="zkpa-error-reload-btn"
              variant="secondary"
              size="sm"
              onClick={() => {
                this.setState({ hasError: false, errorMessage: "" });
                window.location.reload();
              }}
            >
              Tải lại trang
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
